/**
 * Connected trace across a pipe network.
 *
 * WHAT KIND OF TRACE THIS IS
 * Geometric, not topological. A real utility-network trace reads a
 * connectivity model published by the service — junctions, terminals, flow
 * direction, device state — and ArcGIS exposes one only on a Utility
 * Network-enabled service. A plain FeatureServer or WMS layer carries no such
 * model, so connectivity is inferred from the geometry itself: two lines are
 * connected when an endpoint of one lands on an endpoint of the other, within
 * a tolerance.
 *
 * That is exactly how a drafter would read the drawing, and it is right far
 * more often than not — but it is worth being plain about the difference:
 *
 *  - It cannot know flow DIRECTION, so a trace runs in every direction at
 *    once. There is no upstream/downstream without an attribute saying which.
 *  - It cannot know a valve is CLOSED, so it traces straight through one.
 *  - Two pipes that cross without a shared endpoint are not connected here —
 *    correctly, since a crossing is not a junction.
 *  - Two pipes that end near each other ARE connected, if within tolerance.
 *    Tolerance is the whole judgement call: too small and a drawing with
 *    slight gaps falls apart, too large and separate mains fuse together.
 */

const EARTH_RADIUS_M = 6371008.8;

/** Great-circle metres between two [lng, lat] positions. */
function metresBetween(a: [number, number], b: [number, number]): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Degrees of longitude that span a given ground distance at this latitude. */
function degreesFor(metres: number, latitude: number): number {
  const metresPerDegree = 111320 * Math.max(Math.cos((latitude * Math.PI) / 180), 0.01);
  return metres / metresPerDegree;
}

/** Every coordinate of a geometry, whatever its nesting. */
function positionsOf(geometry: GeoJSON.Geometry | null | undefined): [number, number][] {
  const out: [number, number][] = [];
  const walk = (value: any) => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number' && typeof value[1] === 'number') {
      out.push([value[0], value[1]]);
      return;
    }
    for (const part of value) walk(part);
  };
  walk((geometry as any)?.coordinates);
  return out;
}

/** The ends of each part of a line. Only ends can be junctions. */
function endpointsOf(geometry: GeoJSON.Geometry | null | undefined): [number, number][] {
  if (!geometry) return [];
  if (geometry.type === 'LineString') {
    const line = geometry.coordinates as [number, number][];
    return line.length >= 2 ? [line[0], line[line.length - 1]] : [];
  }
  if (geometry.type === 'MultiLineString') {
    return (geometry.coordinates as [number, number][][])
      .filter((part) => part.length >= 2)
      .flatMap((part) => [part[0], part[part.length - 1]]);
  }
  return [];
}

/**
 * The closest point ON a line SEGMENT to `point` (not just one of its two
 * ends), plus the distance to it in metres. A local flat-earth projection
 * around the point's own latitude — accurate enough at the metre-scale
 * tolerances a trace works at, and far cheaper than a proper geodesic
 * projection. `metresToSegment` is the distance half of this on its own,
 * kept as a separate export since most callers only need that.
 */
function closestPointOnSegment(
  point: [number, number],
  a: [number, number],
  b: [number, number],
): { metres: number; point: [number, number] } {
  const latRad = (point[1] * Math.PI) / 180;
  const metresPerDegLng = 111320 * Math.max(Math.cos(latRad), 0.01);
  const metresPerDegLat = 111320;
  const toLocal = (p: [number, number]): [number, number] => [
    (p[0] - point[0]) * metresPerDegLng,
    (p[1] - point[1]) * metresPerDegLat,
  ];
  const [ax, ay] = toLocal(a);
  const [bx, by] = toLocal(b);
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  // Point sits at the local origin, so `point - a` is simply `-a`.
  let t = lengthSq === 0 ? 0 : (-ax * dx - ay * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  const localX = ax + t * dx;
  const localY = ay + t * dy;
  return {
    metres: Math.hypot(localX, localY),
    // Back out of the local frame into real [lng, lat] — the inverse of `toLocal`.
    point: [point[0] + localX / metresPerDegLng, point[1] + localY / metresPerDegLat],
  };
}

function metresToSegment(point: [number, number], a: [number, number], b: [number, number]): number {
  return closestPointOnSegment(point, a, b).metres;
}

/**
 * Whether a barrier lies anywhere along this geometry's path — not only at
 * its endpoints. A valve is usually a point overlaid partway along a single
 * long pipe feature, not a vertex splitting that pipe into two, so checking
 * only endpoints (the ORIGINAL approach here) missed the common case
 * entirely: a barrier dropped mid-pipe had no effect and the trace ran
 * straight through it.
 */
function nearBarrier(
  geometry: GeoJSON.Geometry | null | undefined,
  barriers: [number, number][],
  toleranceMetres: number,
): boolean {
  if (!geometry || barriers.length === 0) return false;
  const parts: [number, number][][] =
    geometry.type === 'LineString'
      ? [geometry.coordinates as [number, number][]]
      : geometry.type === 'MultiLineString'
        ? (geometry.coordinates as [number, number][][])
        : [];
  for (const part of parts) {
    for (let index = 1; index < part.length; index += 1) {
      const a = part[index - 1];
      const b = part[index];
      for (const barrier of barriers) {
        if (metresToSegment(barrier, a, b) <= toleranceMetres) return true;
      }
    }
  }
  return false;
}

/** Total ground length of a line feature, in metres. */
export function lineLengthOf(geometry: GeoJSON.Geometry | null | undefined): number {
  if (!geometry) return 0;
  const parts: [number, number][][] =
    geometry.type === 'LineString'
      ? [geometry.coordinates as [number, number][]]
      : geometry.type === 'MultiLineString'
        ? (geometry.coordinates as [number, number][][])
        : [];
  let total = 0;
  for (const part of parts) {
    for (let index = 1; index < part.length; index += 1) {
      total += metresBetween(part[index - 1], part[index]);
    }
  }
  return total;
}

/**
 * Every OTHER edge whose path comes within tolerance of `point` — tried as an
 * exact endpoint match first (the common case, and the only kind the
 * network's spatial grid can answer without a full scan), then, only when
 * that finds nothing, a full check against every edge's own path.
 *
 * The second pass is what makes a service line tee into a main actually
 * trace. A lateral's own endpoint sits on the MIDDLE of the main's segment —
 * at the tee fitting, not on one of the main's digitised vertices — so
 * endpoint-to-endpoint matching alone never sees it as connected, exactly the
 * gap a real utility drawing's T-connections fall into. This does not weaken
 * "a crossing is not a junction": `point` is always one edge's own endpoint
 * (where it terminates), never an arbitrary point along its length, so two
 * mains that merely cross — where neither actually ends at the crossing —
 * are still never joined here. It is a full O(edges) scan, but only ever
 * runs for an endpoint the fast grid lookup could not already answer, so a
 * normally-digitised network (most junctions share an exact vertex) pays
 * nothing extra.
 */
function neighboursOf(
  network: TraceEdge[],
  endpointGrid: Grid<number>,
  endpoint: [number, number],
  toleranceMetres: number,
  exclude: number,
): number[] {
  const exact = Array.from(
    new Set(
      endpointGrid
        .near(endpoint)
        .filter((candidate) => candidate !== exclude)
        .filter((candidate) =>
          endpointsOf(network[candidate].feature.geometry).some(
            (other) => metresBetween(other, endpoint) <= toleranceMetres,
          ),
        ),
    ),
  );
  if (exact.length > 0) return exact;

  const tee: number[] = [];
  network.forEach((edge, index) => {
    if (index === exclude) return;
    const geometry = edge.feature.geometry;
    const parts: [number, number][][] =
      geometry?.type === 'LineString'
        ? [geometry.coordinates as [number, number][]]
        : geometry?.type === 'MultiLineString'
          ? (geometry.coordinates as [number, number][][])
          : [];
    for (const part of parts) {
      for (let i = 1; i < part.length; i += 1) {
        if (metresToSegment(endpoint, part[i - 1], part[i]) <= toleranceMetres) {
          tee.push(index);
          return;
        }
      }
    }
  });
  return tee;
}

/**
 * The bidirectional counterpart to `neighboursOf`'s per-endpoint fallback.
 *
 * `neighboursOf` catches a lateral discovering the main it tees into, because
 * the tee point IS that lateral's own endpoint. It cannot catch the other
 * direction — the main discovering that lateral — because the tee point is
 * buried mid-segment on the MAIN, not at either of the main's own two
 * endpoints, and the walk only ever asks "what touches one of THIS edge's
 * endpoints". A trace started on the main (the ordinary case: someone flags a
 * break on the distribution line, not on a service lateral) needs that
 * direction too, so this is computed once, for every edge's whole path, up
 * front.
 *
 * Grid-assisted: rather than checking every edge against every other edge's
 * whole path (quadratic in network size), each edge's own segments are
 * sampled roughly every `toleranceDegrees` and only the grid's actual nearby
 * endpoints are verified — cheap for a normally-digitised network where most
 * samples land near nothing.
 */
function buildTeeIndex(
  network: TraceEdge[],
  endpointGrid: Grid<number>,
  toleranceDegrees: number,
  toleranceMetres: number,
): number[][] {
  const result: number[][] = network.map(() => []);
  const link = (a: number, b: number) => {
    if (!result[a].includes(b)) result[a].push(b);
    if (!result[b].includes(a)) result[b].push(a);
  };

  network.forEach((edge, hostIndex) => {
    const geometry = edge.feature.geometry;
    const parts: [number, number][][] =
      geometry?.type === 'LineString'
        ? [geometry.coordinates as [number, number][]]
        : geometry?.type === 'MultiLineString'
          ? (geometry.coordinates as [number, number][][])
          : [];

    for (const part of parts) {
      for (let i = 1; i < part.length; i += 1) {
        const a = part[i - 1];
        const b = part[i];
        const segmentDegrees = Math.hypot(b[0] - a[0], b[1] - a[1]);
        const steps = Math.min(50, Math.max(1, Math.ceil(segmentDegrees / Math.max(toleranceDegrees, 1e-9))));

        for (let step = 0; step <= steps; step += 1) {
          const t = step / steps;
          const sample: [number, number] = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

          for (const candidate of endpointGrid.near(sample)) {
            if (candidate === hostIndex) continue;
            const touches = endpointsOf(network[candidate].feature.geometry).some(
              (endpoint) => metresToSegment(endpoint, a, b) <= toleranceMetres,
            );
            if (touches) link(hostIndex, candidate);
          }
        }
      }
    }
  });

  return result;
}

/** One line in the network, with the layer it came from. */
export interface TraceEdge {
  layerId: string;
  feature: GeoJSON.Feature;
}

export interface TraceResult {
  /** Lines reached, in the order they were reached. */
  edges: TraceEdge[];
  /** Point features sitting on the traced run. */
  assets: { layerId: string; feature: GeoJSON.Feature }[];
  totalMetres: number;
  /** True when the walk stopped at the cap rather than at the network's end. */
  truncated: boolean;
  /** How many junctions a barrier stopped the walk at. */
  blockedAt: number;
}

/**
 * A grid index, so "what is near this point" does not mean scanning everything.
 *
 * Cell size is the tolerance, so a lookup only ever has to check the nine
 * cells around a position. Without it a 2,000-pipe network is four million
 * comparisons, and the panel would appear to hang.
 */
class Grid<T> {
  private cells: Record<string, T[]> = {};

  constructor(private readonly size: number) {}

  private keyFor(x: number, y: number) {
    return `${Math.floor(x / this.size)}:${Math.floor(y / this.size)}`;
  }

  add(position: [number, number], value: T) {
    const key = this.keyFor(position[0], position[1]);
    (this.cells[key] ||= []).push(value);
  }

  near(position: [number, number]): T[] {
    const cx = Math.floor(position[0] / this.size);
    const cy = Math.floor(position[1] / this.size);
    const found: T[] = [];
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        const bucket = this.cells[`${cx + dx}:${cy + dy}`];
        if (bucket) found.push(...bucket);
      }
    }
    return found;
  }
}

export interface TraceOptions {
  /** Line features to walk, tagged with their layer. */
  network: TraceEdge[];
  /** Point features to attach where they sit on the traced run. */
  assets: { layerId: string; feature: GeoJSON.Feature }[];
  /** How close two ends must be to count as joined, in metres. */
  toleranceMetres: number;
  /** Stops runaway traces on a badly snapped drawing. */
  maxEdges: number;
  /**
   * Junctions the trace may not pass through, as [lng, lat].
   *
   * The answer to what a geometric trace cannot know. It cannot read that a
   * valve is shut, so instead of guessing, the person who DOES know places a
   * barrier there and the walk stops. Barriers block at the junction — a pipe
   * already reached stays reached; what lies beyond the barrier does not.
   */
  barriers?: [number, number][];
}

/**
 * Point assets sitting anywhere ALONG the given edges' own paths, not only
 * at one of their digitised vertices — a valve or a service tee is usually
 * installed partway along a main's run, and a real pipe is commonly
 * vertexed only every 70-90 m (see `nearestEdge`'s own doc on the same
 * sparse-digitisation shape). Checking vertex-to-vertex distance alone, as
 * this used to, meant whether a given point attached was mostly down to
 * chance — whether it happened to land exactly on one of those few
 * vertices — which is what made asset attachment look inconsistent from
 * one point to the next despite all of them sitting visibly on the line.
 *
 * Broad-phase only: each edge's path is sampled into a grid (the same
 * technique `buildTeeIndex` already uses for this exact "near a path, not
 * a point" problem), and only the few candidate edges that turns up are
 * then checked precisely, via `nearestEdge`'s own real segment-distance
 * math, so the result is exact — the sampling only limits what gets
 * re-checked, never what counts as a match.
 *
 * Shared by `traceConnected`, `traceDisconnected`, and `traceIsolation`,
 * which differ only in WHICH edges they hand it.
 */
function attachAssets(
  edges: TraceEdge[],
  assets: { layerId: string; feature: GeoJSON.Feature }[],
  toleranceDegrees: number,
  toleranceMetres: number,
): { layerId: string; feature: GeoJSON.Feature }[] {
  if (edges.length === 0 || assets.length === 0) return [];

  const pathGrid = new Grid<number>(Math.max(toleranceDegrees, 1e-7));
  edges.forEach((edge, index) => {
    const geometry = edge.feature.geometry;
    const parts: [number, number][][] =
      geometry?.type === 'LineString'
        ? [geometry.coordinates as [number, number][]]
        : geometry?.type === 'MultiLineString'
          ? (geometry.coordinates as [number, number][][])
          : [];
    for (const part of parts) {
      for (let i = 1; i < part.length; i += 1) {
        const a = part[i - 1];
        const b = part[i];
        const segmentDegrees = Math.hypot(b[0] - a[0], b[1] - a[1]);
        const steps = Math.min(50, Math.max(1, Math.ceil(segmentDegrees / Math.max(toleranceDegrees, 1e-9))));
        for (let step = 0; step <= steps; step += 1) {
          const t = step / steps;
          pathGrid.add([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t], index);
        }
      }
    }
  });

  return assets.filter((asset) =>
    positionsOf(asset.feature.geometry).some((position) => {
      const candidates = Array.from(new Set(pathGrid.near(position))).map((index) => edges[index]);
      return (
        candidates.length > 0 &&
        nearestEdge(candidates, { lat: position[1], lng: position[0] }, toleranceMetres) >= 0
      );
    }),
  );
}

/**
 * The breadth-first walk itself, shared by `traceConnected` (which reports
 * what it REACHED) and `traceDisconnected` (which reports everything it
 * DID NOT) — both ask the exact same question of the network, they just
 * read the opposite half of the same answer.
 *
 * Breadth-first from every start at once, so `order` comes back in rings
 * around them — which is also the order someone reads it in when asking "how
 * far does this go". Several starts share one visited set, so a run reachable
 * from two of them is counted once, not twice.
 */
function walkConnected(
  startIndexes: number[],
  options: TraceOptions,
): { toleranceDegrees: number; visited: Set<number>; order: number[]; truncated: boolean; blockedAt: number } {
  const { network, toleranceMetres, maxEdges, barriers = [] } = options;
  const starts = startIndexes.filter((index) => index >= 0 && index < network.length);
  if (starts.length === 0) {
    return { toleranceDegrees: 0, visited: new Set(), order: [], truncated: false, blockedAt: 0 };
  }

  const latitude = endpointsOf(network[starts[0]].feature.geometry)[0]?.[1] ?? 0;
  const toleranceDegrees = degreesFor(toleranceMetres, latitude);
  const cellSize = Math.max(toleranceDegrees, 1e-7);

  // Index every line by its endpoints, so a junction lookup is local.
  const endpointGrid = new Grid<number>(cellSize);
  network.forEach((edge, index) => {
    for (const endpoint of endpointsOf(edge.feature.geometry)) endpointGrid.add(endpoint, index);
  });

  /* Service lines (and anything else that tees into the MIDDLE of a main
     rather than sharing one of its digitised vertices) — see buildTeeIndex's
     own doc for why this is the direction `neighboursOf` alone cannot cover. */
  const teeIndex = buildTeeIndex(network, endpointGrid, toleranceDegrees, toleranceMetres);

  /* Computed once per edge, over its WHOLE path — not just its two ends —
     so a valve sitting partway along a single long pipe feature blocks the
     trace exactly like one sitting at a junction between two pipe features
     does. See `nearBarrier`'s own doc for why endpoint-only checking missed
     the common case. */
  const edgeBlocked = network.map((edge) => nearBarrier(edge.feature.geometry, barriers, toleranceMetres));

  const visited = new Set<number>(starts);
  const order: number[] = [...new Set(starts)];
  const queue: number[] = [...order];
  let truncated = false;
  let blockedAt = 0;

  while (queue.length > 0) {
    if (order.length >= maxEdges) {
      truncated = true;
      break;
    }
    const current = queue.shift() as number;

    /* Stop AT the pipe carrying the barrier, not before it — whether that
       barrier sits at one of ITS endpoints (a junction with another pipe) or
       partway along its own path (a valve drawn mid-pipe). The pipe itself is
       already part of the answer (`order` got it when it was reached); what
       this skips is looking for anything CONNECTED past it. */
    if (edgeBlocked[current]) {
      blockedAt += 1;
      continue;
    }

    const candidates = new Set<number>(teeIndex[current]);
    for (const endpoint of endpointsOf(network[current].feature.geometry)) {
      for (const candidate of neighboursOf(network, endpointGrid, endpoint, toleranceMetres, current)) {
        candidates.add(candidate);
      }
    }
    for (const candidate of candidates) {
      if (visited.has(candidate)) continue;
      visited.add(candidate);
      order.push(candidate);
      queue.push(candidate);
    }
  }

  return { toleranceDegrees, visited, order, truncated, blockedAt };
}

/**
 * Walks the network outward from one or more lines and reports what it
 * reached — see `walkConnected`'s own doc for the walk itself.
 */
export function traceConnected(startIndexes: number[], options: TraceOptions): TraceResult {
  const { network, assets, toleranceMetres } = options;
  const { toleranceDegrees, order, truncated, blockedAt } = walkConnected(startIndexes, options);
  const edges = order.map((index) => network[index]);
  const attached = attachAssets(edges, assets, toleranceDegrees, toleranceMetres);

  return {
    edges,
    assets: attached,
    totalMetres: edges.reduce((sum, edge) => sum + lineLengthOf(edge.feature.geometry), 0),
    truncated,
    blockedAt,
  };
}

/**
 * Esri's Utility Network offers this as its own trace type: the exact
 * opposite of Connected — instead of what the walk REACHED from the start
 * points, this reports every OTHER line in the chosen network layers, the
 * part that never came up in that walk at all. A real use: start on the
 * system you know is live, and whatever comes back here is either a
 * genuinely separate system or a digitising gap — two mains that LOOK joined
 * on the drawing but do not actually share an endpoint within tolerance —
 * either way, worth a look.
 *
 * `truncated` carries the same meaning as Connected's own: the WALK that
 * decides what counts as "reached" was cut off at the edge cap before it
 * could finish, so some of what this reports as disconnected may only
 * appear that way because the walk never got far enough to reach it. Read a
 * truncated disconnected list as provisional, the same as a truncated
 * connected one.
 */
export function traceDisconnected(startIndexes: number[], options: TraceOptions): TraceResult {
  const { network, assets, toleranceMetres } = options;
  const { toleranceDegrees, visited, truncated, blockedAt } = walkConnected(startIndexes, options);
  // No valid start at all (as opposed to one that legitimately reached
  // nothing else) — `visited` is seeded from the starts themselves, so it is
  // empty only in this case. Mirrors `traceConnected`'s own early return
  // rather than reporting the WHOLE network as "disconnected" from nothing.
  if (visited.size === 0) {
    return { edges: [], assets: [], totalMetres: 0, truncated: false, blockedAt: 0 };
  }
  const edges = network.filter((_, index) => !visited.has(index));
  const attached = attachAssets(edges, assets, toleranceDegrees, toleranceMetres);

  return {
    edges,
    assets: attached,
    totalMetres: edges.reduce((sum, edge) => sum + lineLengthOf(edge.feature.geometry), 0),
    truncated,
    blockedAt,
  };
}

/**
 * Valve isolation trace.
 *
 * Esri's Utility Network offers this as its own trace type, reading a
 * device's real state off a model this app has no equivalent of — see this
 * file's own opening doc on what a geometric trace can and cannot know.
 * The geometric approximation here is the direct translation: instead of
 * someone manually marking which junctions are shut (plain `traceConnected`
 * barriers), EVERY feature in a chosen valve layer counts as one — an
 * operator cannot act on a plan that assumes it knows which valves happen
 * to be open right now, so the honest answer is "here is every valve that
 * bounds this area; close them all to isolate it."
 *
 * Reports two things `traceConnected` does not:
 *  - WHICH valves actually bounded the isolated run, not just a count —
 *    the whole point being a list someone can act on.
 *  - OPEN ENDS: a traced pipe end with no valve and nothing further
 *    connected. That is a leak in the isolation plan — flow can still
 *    escape there — and silently omitting it would be worse than not
 *    running the trace at all.
 */
export interface IsolationAsset {
  layerId: string;
  feature: GeoJSON.Feature;
}

export interface IsolationResult {
  /** The isolated pipe run. */
  edges: TraceEdge[];
  /** Non-valve point features sitting on the isolated run. */
  assets: IsolationAsset[];
  /** The valves that bound the isolated area — close these to isolate it. */
  isolationValves: IsolationAsset[];
  totalMetres: number;
  /** True when the walk stopped at the cap rather than at every valve. */
  truncated: boolean;
  /**
   * Traced pipe ends with no valve and nothing further connected — the
   * isolated area is NOT fully sealed off at these points.
   */
  openEnds: [number, number][];
}

export interface IsolationOptions {
  network: TraceEdge[];
  /** Every feature here is treated as a closed valve. */
  valves: IsolationAsset[];
  /** Point features (other than valves) to attach where they sit on the run. */
  assets: IsolationAsset[];
  toleranceMetres: number;
  maxEdges: number;
}

export function traceIsolation(startIndexes: number[], options: IsolationOptions): IsolationResult {
  const { network, valves, assets, toleranceMetres, maxEdges } = options;
  const starts = startIndexes.filter((index) => index >= 0 && index < network.length);
  if (starts.length === 0) {
    return { edges: [], assets: [], isolationValves: [], totalMetres: 0, truncated: false, openEnds: [] };
  }

  const latitude = endpointsOf(network[starts[0]].feature.geometry)[0]?.[1] ?? 0;
  const toleranceDegrees = degreesFor(toleranceMetres, latitude);
  const cellSize = Math.max(toleranceDegrees, 1e-7);

  const endpointGrid = new Grid<number>(cellSize);
  network.forEach((edge, index) => {
    for (const endpoint of endpointsOf(edge.feature.geometry)) endpointGrid.add(endpoint, index);
  });

  /* Service lines teeing into the middle of a main — see buildTeeIndex's own
     doc for why this is the direction `neighboursOf` alone cannot cover. */
  const teeIndex = buildTeeIndex(network, endpointGrid, toleranceDegrees, toleranceMetres);

  // Each valve indexed at its own position, so a blocked edge (or an open
  // end) can be traced back to the SPECIFIC valve nearest it — not just
  // "something" blocked it.
  const valveGrid = new Grid<number>(cellSize);
  const valvePositions: [number, number][] = [];
  valves.forEach((valve, index) => {
    const position = positionsOf(valve.feature.geometry)[0];
    if (!position) return;
    valveGrid.add(position, index);
    valvePositions.push(position);
  });

  function nearestValveTo(point: [number, number]): number {
    let best = -1;
    let bestDistance = toleranceMetres;
    for (const index of valveGrid.near(point)) {
      const position = positionsOf(valves[index].feature.geometry)[0];
      if (!position) continue;
      const distance = metresBetween(point, position);
      if (distance <= bestDistance) {
        bestDistance = distance;
        best = index;
      }
    }
    return best;
  }

  /**
   * Which valve (if any) blocks each edge, and WHERE along it — checked
   * over its WHOLE path, same reasoning as `nearBarrier` above: a valve
   * usually sits partway along one long pipe feature, not at a vertex
   * splitting it in two.
   *
   * The POSITION is kept, not just which valve, because a long edge can
   * easily have the valve near only ONE of its two ends — a pipe spanning
   * a whole block with a valve right at its far corner is a completely
   * normal shape for real survey data. The near endpoint (whichever is
   * closer to the valve) is genuinely blocked; the far endpoint is not,
   * and still needs its own connectivity checked below rather than being
   * silently treated as settled just because SOMETHING on this edge was.
   */
  const blockedBy: ({ valveIndex: number; valvePosition: [number, number] } | null)[] = network.map(
    (edge) => {
      const geometry = edge.feature.geometry;
      const parts: [number, number][][] =
        geometry?.type === 'LineString'
          ? [geometry.coordinates as [number, number][]]
          : geometry?.type === 'MultiLineString'
            ? (geometry.coordinates as [number, number][][])
            : [];
      for (const part of parts) {
        for (let index = 1; index < part.length; index += 1) {
          for (const valvePosition of valvePositions) {
            if (metresToSegment(valvePosition, part[index - 1], part[index]) <= toleranceMetres) {
              return { valveIndex: nearestValveTo(valvePosition), valvePosition };
            }
          }
        }
      }
      return null;
    },
  );

  const visited = new Set<number>(starts);
  const order: number[] = [...new Set(starts)];
  const queue: number[] = [...order];
  let truncated = false;
  const hitValveIndexes = new Set<number>();
  const openEnds: [number, number][] = [];

  /** Looks past ONE endpoint of an edge: joins an unvisited neighbour onto
   *  the walk, or — nothing there at all — records a valve if one sits
   *  exactly at this endpoint, or an open end otherwise. Shared by the
   *  unblocked path below and the "far side of a blocked edge" case, which
   *  both ask the exact same question of one endpoint at a time. */
  function exploreEndpoint(endpoint: [number, number], from: number): void {
    const neighbours = neighboursOf(network, endpointGrid, endpoint, toleranceMetres, from);

    if (neighbours.length === 0) {
      // Nothing joins here. A valve sitting exactly AT this endpoint
      // (rather than partway along an edge, which `blockedBy` already
      // catches) still isolates it; only a bare dead end is a real leak.
      const valveHere = nearestValveTo(endpoint);
      if (valveHere >= 0) hitValveIndexes.add(valveHere);
      else openEnds.push(endpoint);
      return;
    }

    for (const candidate of neighbours) {
      if (visited.has(candidate)) continue;
      visited.add(candidate);
      order.push(candidate);
      queue.push(candidate);
    }
  }

  while (queue.length > 0) {
    if (order.length >= maxEdges) {
      truncated = true;
      break;
    }
    const current = queue.shift() as number;
    const block = blockedBy[current];

    if (block) {
      hitValveIndexes.add(block.valveIndex);
      /*
       * A non-start edge was reached by entering at ONE specific endpoint,
       * so a valve ANYWHERE along it blocks the one direction that matters
       * — continuing on to the OTHER endpoint — exactly like
       * traceConnected's own barrier handling: stop at the pipe carrying
       * it, do not look past it.
       *
       * A START edge is different: the break can sit anywhere along its
       * length, with no "entered from" side at all, so each of its two
       * ends is its OWN question. A long pipe with the valve near one end
       * (its far corner, say) and the break near the other is a completely
       * ordinary shape — the end on the OTHER side of the valve from
       * wherever that valve sits is simply not affected by it, and still
       * needs its own connectivity (or open-end) checked, rather than
       * being silently skipped just because something elsewhere on the
       * same feature was blocked. (Found testing against a real imported
       * network: a 1.3 km main with one valve at its far end otherwise
       * reported zero open ends, because the near-end check never ran.)
       */
      if (starts.includes(current)) {
        const endpoints = endpointsOf(network[current].feature.geometry);
        if (endpoints.length === 2) {
          const [first, last] = endpoints;
          const farEndpoint =
            metresBetween(first, block.valvePosition) > metresBetween(last, block.valvePosition)
              ? first
              : last;
          exploreEndpoint(farEndpoint, current);
        }
      }
      continue;
    }

    for (const candidate of teeIndex[current]) {
      if (visited.has(candidate)) continue;
      visited.add(candidate);
      order.push(candidate);
      queue.push(candidate);
    }

    for (const endpoint of endpointsOf(network[current].feature.geometry)) {
      exploreEndpoint(endpoint, current);
    }
  }

  const edges = order.map((index) => network[index]);
  const attachedAssets = attachAssets(edges, assets, toleranceDegrees, toleranceMetres);

  return {
    edges,
    assets: attachedAssets,
    isolationValves: Array.from(hitValveIndexes).map((index) => valves[index]),
    totalMetres: edges.reduce((sum, edge) => sum + lineLengthOf(edge.feature.geometry), 0),
    truncated,
    openEnds,
  };
}

/**
 * The line nearest a clicked point, or -1 when nothing is close enough.
 *
 * Measured to the nearest SEGMENT, not just the nearest vertex — a flag
 * dropped on the middle of a long, sparsely-vertexed real-world pipe (one
 * vertex every 70-90 m is normal in an imported dataset) can sit metres
 * from the line itself while still being tens of metres from either
 * nearby vertex, which made a flag placed exactly ON a pipe report "no
 * pipe under that flag" purely because of how far apart that pipe's own
 * vertices happened to be. Same fix, same reasoning, as the Select tool's
 * own box-must-touch-a-vertex bug.
 */
export function nearestEdge(
  network: TraceEdge[],
  point: { lat: number; lng: number },
  withinMetres: number,
): number {
  let best = -1;
  let bestDistance = withinMetres;
  const target: [number, number] = [point.lng, point.lat];
  network.forEach((edge, index) => {
    const geometry = edge.feature.geometry;
    const parts: [number, number][][] =
      geometry?.type === 'LineString'
        ? [geometry.coordinates as [number, number][]]
        : geometry?.type === 'MultiLineString'
          ? (geometry.coordinates as [number, number][][])
          : [];
    for (const part of parts) {
      for (let i = 1; i < part.length; i += 1) {
        const metres = metresToSegment(target, part[i - 1], part[i]);
        if (metres < bestDistance) {
          bestDistance = metres;
          best = index;
        }
      }
    }
  });
  return best;
}
